import { AxiosResponse } from "axios";
import axiosInstance from "../../../middlewares/axiosInstance";
import {
  StockMovementLogsRequest,
  StockMovementLogsResponse,
  StockMovementSummaryRequest,
  StockMovementSummaryResponse,
  APIResponse,
} from "../types/stock-movement-logs.types";

class StockMovementLogsService {
  private baseUrl = "/business-admin/stock-movement-logs";

  /**
   * Get stock movement logs with filtering and pagination
   */
  async getStockMovementLogs(
    request: StockMovementLogsRequest = {}
  ): Promise<AxiosResponse<APIResponse<StockMovementLogsResponse>>> {
    const requestBody = {
      page: request.page || 1,
      page_size: request.page_size || 20,
      filters: request.filters || {},
      sort_field: request.sort_field || "movement_date",
      sort_order: request.sort_order || "desc",
      calculate_balances: request.calculate_balances || false,
    };

    return axiosInstance.post(`${this.baseUrl}/list`, requestBody);
  }

  /**
   * Get stock movement summary by product
   */
  async getStockMovementSummary(
    request: StockMovementSummaryRequest = {}
  ): Promise<AxiosResponse<APIResponse<StockMovementSummaryResponse>>> {
    return axiosInstance.post(`${this.baseUrl}/summary`, request);
  }

  /**
   * Get recent stock movements (last 7 days)
   */
  async getRecentMovements(
    pageSize: number = 20,
    calculateBalances: boolean = false
  ): Promise<AxiosResponse<APIResponse<StockMovementLogsResponse>>> {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

    return this.getStockMovementLogs({
      page: 1,
      page_size: pageSize,
      filters: {
        date_range: {
          start: sevenDaysAgo,
          end: Date.now(),
        },
      },
      calculate_balances: calculateBalances,
    });
  }

  /**
   * Get movement history for a specific product
   */
  async getProductMovementHistory(
    productId: string,
    dateRange?: { start: number; end: number },
    calculateBalances: boolean = true
  ): Promise<AxiosResponse<APIResponse<StockMovementLogsResponse>>> {
    const defaultDateRange = {
      start: Date.now() - 30 * 24 * 60 * 60 * 1000, // Last 30 days
      end: Date.now(),
    };

    return this.getStockMovementLogs({
      page: 1,
      page_size: 100,
      filters: {
        product_id: productId,
        date_range: dateRange || defaultDateRange,
      },
      sort_field: "movement_date",
      sort_order: "asc", // Ascending for proper balance calculation
      calculate_balances: calculateBalances,
    });
  }

  /**
   * Search stock movements
   */
  async searchMovements(
    searchTerm: string,
    filters: Partial<StockMovementLogsRequest["filters"]> = {},
    page: number = 1,
    pageSize: number = 20
  ): Promise<AxiosResponse<APIResponse<StockMovementLogsResponse>>> {
    return this.getStockMovementLogs({
      page,
      page_size: pageSize,
      filters: {
        ...filters,
        search: searchTerm,
      },
    });
  }

  /**
   * Get movements by source type
   */
  async getMovementsBySourceType(
    sourceType:
      | "GRN"
      | "TRANSFER"
      | "ADJUSTMENT"
      | "PRODUCTION"
      | "SALE"
      | "WASTAGE",
    dateRange?: { start: number; end: number },
    page: number = 1,
    pageSize: number = 20
  ): Promise<AxiosResponse<APIResponse<StockMovementLogsResponse>>> {
    return this.getStockMovementLogs({
      page,
      page_size: pageSize,
      filters: {
        source_type: sourceType,
        date_range: dateRange,
      },
    });
  }

  /**
   * Get movements by warehouse location
   */
  async getMovementsByWarehouse(
    warehouseLocation: string,
    dateRange?: { start: number; end: number },
    page: number = 1,
    pageSize: number = 20
  ): Promise<AxiosResponse<APIResponse<StockMovementLogsResponse>>> {
    return this.getStockMovementLogs({
      page,
      page_size: pageSize,
      filters: {
        warehouse_location: warehouseLocation,
        date_range: dateRange,
      },
    });
  }

  /**
   * Export stock movement logs (placeholder for future implementation)
   */
  async exportMovements(
    format: "excel" | "csv" = "excel",
    filters: StockMovementLogsRequest["filters"] = {}
  ): Promise<AxiosResponse<APIResponse<{ download_url: string }>>> {
    // This would be implemented when the backend supports export
    return axiosInstance.post(`${this.baseUrl}/export`, {
      format,
      filters,
    });
  }
}

export const stockMovementLogsService = new StockMovementLogsService();
export default stockMovementLogsService;
